import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setBreadcrumb } from '../../features/breadcrumb/breadcrumbSlice'
import { selectAssetProfileId } from '../../features/assetProfile/assetProfileSlice'
import { formatDateTime } from '../../utils/date'
import Api from '../../api/endPoints'
import { mapBmsHistoryToPoints } from '../../api/bmsHistoryMapper'
import { computeQuickRangeBounds } from '../../utils/timeRange'
import TimeRangeDropdown from './TimeRangeDropdown'
import { useBmsSyncSocket, mapNsyncUpdateToBmsDetail, mapBmsItemToBmsDetail } from '../../socket/bmsSyncSocket'
import { useDeviceDashboardSocket } from '../../socket/deviceDashboardSocket'
import { useTelemetryHistory, buildTelemetryChart, mergeIntoWindow, WINDOW_MS } from '../../socket/useTelemetryHistory'
import StatusOverviewCard from './StatusOverviewCard'
import IndicatorsCard from './IndicatorsCard'
import VersionInfoCard from './VersionInfoCard'
import ConfigsCard from './ConfigsCard'
import CycleInfoCard from './CycleInfoCard'
import FaultsCard from './FaultsCard'
import CapacityCard from './CapacityCard'
import FlagsAndFaultsCard from './FlagsAndFaultsCard'
import TemperatureStatsCard from './TemperatureStatsCard'
import CellVoltagesCard from './CellVoltagesCard'
import NtcTemperaturesCard from './NtcTemperaturesCard'
import TelemetryChart from './TelemetryChart'
import SocPackVoltageChart from './SocPackVoltageChart'
import AllDataTable from './AllDataTable'

const TELEMETRY_SERIES = [
  { key: 'SoC', unit: '%' },
  { key: 'Pack Voltage', unit: ' V' },
  { key: 'Current', unit: ' A' },
]

const pickDefined = (obj = {}) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined))

// /nsync/devicesLatest returns every BMS node under the VIM; the node whose
// nodeId matches the :ecuid route param is the battery this dashboard shows.
// No fallback to nodes[0] on a miss — showing another battery's timestamp
// would misrepresent how fresh this one's data is.
const findNodeLastSeen = (device, ecuid) =>
  device?.nodes?.find((node) => node.nodeId === ecuid)?.lastSeen

const BMS_PLACEHOLDERS = {
  organization: '--',
  packVoltage: '--',
  packCurrent: '--',
  firmwareVersion: '--',
  hardwareVersion: '--',
  protocol: '--',
  mcuType: '--',
  cycleCount: '--',
  chargeCycle: '--',
  dischargeCycle: '--',
  balancingFlags: '--',
  ahGained: '--',
  ahSpent: '--',
  whSpent: '--',
  fullCapacity: '--',
  remainingCap: '--',
  afeTemperature: '--',
  boardTemperature: '--',
  mosfetTemperature: '--',
  prechargeTemperature: '--',
  balancingTemperature: '--',
  shuntTemperature: '--',
  pchgFail: '--',
  extraFault: '--',
}

const BmsDashboard = () => {
  const { deviceId, ecuid } = useParams()
  const navigate = useNavigate()
  const [timeRange, setTimeRange] = useState(() => ({ label: 'Last 30 min - Realtime', ...computeQuickRangeBounds('Last 30 min - Realtime') }))
  const dispatch = useDispatch()
  const [bmsHistory, setBmsHistory] = useState(null)
  // Drives the charts' and All Data table's loading/error states — a wide range
  // (a month of snapshots) can take seconds, and until this landed the charts
  // and table just sat blank, indistinguishable from "no data in this range".
  const [isHistoryLoading, setIsHistoryLoading] = useState(true)
  const [historyError, setHistoryError] = useState(null)
  const [isRefetching, setIsRefetching] = useState(false)
  const [apiBmsDetail, setApiBmsDetail] = useState(null)
  const [nodeLastSeen, setNodeLastSeen] = useState(null)
  const [vimOrgName, setVimOrgName] = useState(null)
  // Fetched once per session by Layout — null until it resolves.
  const profileID = useSelector(selectAssetProfileId)


  useEffect(() => {
    if (!deviceId || profileID == null) return
    let cancelled = false

    const fetchOrgName = async () => {
      try {
        const { status, data } = await Api.getProfileDevices(profileID)
        const item = (data?.items ?? []).find((i) => i.deviceId === deviceId)
        if (!cancelled && status === 200 && item?.orgName) setVimOrgName(item.orgName)
      } catch (error) {
        console.error('Failed to fetch VIM profile devices:', error)
      }
    }

    fetchOrgName()
    return () => { cancelled = true }
  }, [deviceId, profileID])

  useEffect(() => {
    dispatch(setBreadcrumb([
      { title: 'VIM List', link: '/vim-list' },
      { title: deviceId, link: `/ViewDashboard/${deviceId}` },
      { title: ecuid, link: `/ViewDashboard/${deviceId}/bms/${ecuid}`, current: true },
    ]))
  }, [dispatch, deviceId, ecuid])

  useEffect(() => {
    if (!ecuid) return
    let cancelled = false

    const fetchBmsItem = async () => {
      try {
        const { status, data } = await Api.getBmsLatest(ecuid)
        if (!cancelled && status === 200 && data) setApiBmsDetail({ ...mapBmsItemToBmsDetail(data), lastSeen: data.lastSeen })
      } catch (error) {
        console.error('Error fetching BMS device:', error)
      }
    }

    fetchBmsItem()
    return () => { cancelled = true }
  }, [ecuid])

  // Seeds "Last updated at" from the node's own lastSeen so the header shows a
  // real timestamp on entry, instead of '--' until the socket's first payload.
  useEffect(() => {
    if (!deviceId || !ecuid) return
    let cancelled = false

    const fetchNodeLastSeen = async () => {
      try {
        const { status, data } = await Api.getDevicesLatest(deviceId)
        if (!cancelled && status === 200) setNodeLastSeen(findNodeLastSeen(data, ecuid) ?? null)
      } catch (error) {
        console.error('Error fetching device latest snapshot:', error)
      }
    }

    fetchNodeLastSeen()
    return () => { cancelled = true }
  }, [deviceId, ecuid])

  // A time-range change refetches, so overlapping requests are routine here:
  // `cancelled` keeps a superseded response from overwriting fresher data or
  // clearing the loading flag while the newer request is still open.
  useEffect(() => {
    if (!ecuid || !timeRange.from || !timeRange.to) {
      setBmsHistory(null)
      setIsHistoryLoading(false)
      return
    }
    let cancelled = false

    const fetchBmsHistory = async () => {
      setIsHistoryLoading(true)
      setHistoryError(null)
      try {
        const { status, data } = await Api.getBmsHistory(ecuid, timeRange.from.toISOString(), timeRange.to.toISOString())
        if (cancelled) return
        // `request` resolves for every status instead of throwing, so an HTTP
        // error never reaches the catch below — without this check a 500 would
        // read as "no data in this time range" rather than as a failure.
        if (status !== 200) {
          console.error('BMS history request failed with status', status)
          setHistoryError("Couldn't load telemetry history")
          return
        }
        setBmsHistory(Array.isArray(data) ? data : null)
      } catch (error) {
        console.error('Failed to fetch BMS history:', error)
        if (!cancelled) setHistoryError("Couldn't load telemetry history")
      } finally {
        if (!cancelled) setIsHistoryLoading(false)
      }
    }

    fetchBmsHistory()
    return () => { cancelled = true }
  }, [ecuid, timeRange])

  const isRealtime = timeRange.label === 'Last 30 min - Realtime'
  const { data: liveData } = useBmsSyncSocket(deviceId, ecuid, isRealtime)
  const MIN_REFETCH_MS = 1200

  // Also serves as the charts'/table's Retry action, so it has to clear and
  // re-set historyError — otherwise a failed load would keep showing its error
  // marker even after a successful retry.
  const handleRefetch = async () => {
    if (isRefetching || !ecuid) return
    setIsRefetching(true)
    setIsHistoryLoading(true)
    setHistoryError(null)
    const startedAt = Date.now()
    try {
      const quick = computeQuickRangeBounds(timeRange.label)
      const from = quick.from ?? timeRange.from
      const to = quick.to ?? timeRange.to
      if (!from || !to) return
      const [historyResponse, latestResponse, deviceLatestResponse] = await Promise.all([
        Api.getBmsHistory(ecuid, from.toISOString(), to.toISOString()),
        Api.getBmsLatest(ecuid),
        deviceId ? Api.getDevicesLatest(deviceId) : Promise.resolve({ status: 0 }),
      ])
      if (historyResponse.status === 200) {
        setBmsHistory(Array.isArray(historyResponse.data) ? historyResponse.data : null)
      } else {
        console.error('BMS history refetch failed with status', historyResponse.status)
        setHistoryError("Couldn't load telemetry history")
      }
      if (latestResponse.status === 200 && latestResponse.data) {
        setApiBmsDetail({ ...mapBmsItemToBmsDetail(latestResponse.data), lastSeen: latestResponse.data.lastSeen })
      }
      if (deviceLatestResponse.status === 200) {
        setNodeLastSeen(findNodeLastSeen(deviceLatestResponse.data, ecuid) ?? null)
      }
    } catch (error) {
      console.error('Failed to refetch BMS data:', error)
      setHistoryError("Couldn't load telemetry history")
    } finally {
      setIsHistoryLoading(false)
      const remaining = MIN_REFETCH_MS - (Date.now() - startedAt)
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining))
      setIsRefetching(false)
    }
  }

  const bmsDetail = mapNsyncUpdateToBmsDetail(liveData, ecuid)
  const bms = { ...BMS_PLACEHOLDERS, ...pickDefined(apiBmsDetail ?? {}), ...pickDefined(bmsDetail), ...(vimOrgName ? { organization: vimOrgName } : {}) }

  const { data: deviceUpdateData } = useDeviceDashboardSocket(deviceId, isRealtime)
  const deviceTs = deviceUpdateData?.rows?.[0]?.deviceInfo?.ts
  const bmsRaw = bmsDetail?.raw || {}
  const liveValues = {
    SoC: bmsDetail?.soc,
    soh: bmsDetail?.soh,
    'Pack Voltage': bmsDetail?.packVoltage !== undefined ? parseFloat(bmsDetail.packVoltage) : undefined,
    Current: bmsDetail?.packCurrent !== undefined ? parseFloat(bmsDetail.packCurrent) : undefined,
    ...Object.fromEntries((bmsDetail?.cellsRaw ?? []).map((v, i) => [`Cell ${i + 1}`, v])),
    ...Object.fromEntries((bmsDetail?.ntcRaw ?? []).map((v, i) => [`NTC ${i + 1}`, v])),
    afeTemp_C: bmsRaw.afeTemp_C,
    boardTemp_C: bmsRaw.boardTemp_C,
    mosfetTemp_C: bmsRaw.mosfetTemp_C,
    prechargeTemp_C: bmsRaw.prechargeTemp_C,
    balancingTemp_C: bmsRaw.balancingTemp_C,
    shuntTemp_C: bmsRaw.shuntTemp_C,
    chgCycles: bmsRaw.chgCycles,
    dischgCycles: bmsRaw.dischgCycles,
    balancingFlags: bmsRaw.balancingFlags,
    ahGained_mAh: bmsRaw.ahGained_mAh,
    ahSpent_mAh: bmsRaw.ahSpent_mAh,
    whSpent: bmsRaw.whSpent,
    fullCapacity_mAh: bmsRaw.fullCapacity_mAh,
    remainingCapacity_mAh: bmsRaw.remainingCapacity_mAh,
    powerMode: bmsRaw.powerMode === 1,
    batteryMode: bmsRaw.batteryMode === 1,
    chargeMosfet: bmsRaw.fetStatus?.chg_on,
    dischargeMosfet: bmsRaw.fetStatus?.dsg_on,
    prechargeMosfet: bmsRaw.fetStatus?.pchg_on,
    fwVersion: bmsRaw.fwVersion,
    protocolMajor: bmsRaw.protocolMajor,
    protocolMinor: bmsRaw.protocolMinor,
    mcuType: bmsRaw.mcuType,
    pchgFailReason: bmsRaw.pchgFailReason,
    faultExtra: bmsRaw.faultExtra,
    faultStatusRaw: bmsRaw.faultStatusRaw,
    cellVoltMax_mV: bmsRaw.cellVoltMax_mV,
    cellVoltMin_mV: bmsRaw.cellVoltMin_mV,
    tempMax_C: bmsRaw.tempMax_C,
    tempMin_C: bmsRaw.tempMin_C,
    ntcCount: bmsRaw.ntcCount,
    faults: bmsRaw.faults,
  }
  const liveHistory = useTelemetryHistory(deviceTs, liveValues)

  const historyPoints = useMemo(
    () => (timeRange.from ? mapBmsHistoryToPoints(bmsHistory) : []),
    [bmsHistory, timeRange.from],
  )
  const activePoints = useMemo(
    () => (isRealtime ? mergeIntoWindow(historyPoints, liveHistory) : historyPoints),
    [isRealtime, historyPoints, liveHistory],
  )

  const countSeries = (points, prefix) => {
    for (const p of points) {
      let count = 0
      while (p[`${prefix} ${count + 1}`] !== undefined) count++
      if (count > 0) return count
    }
    return 0
  }
  const cellSeriesDefs = Array.from({ length: countSeries(activePoints, 'Cell') }, (_, i) => ({ key: `Cell ${i + 1}`, unit: ' V', scale: 0.001 }))
  const ntcSeriesDefs = Array.from({ length: countSeries(activePoints, 'NTC') }, (_, i) => ({ key: `NTC ${i + 1}`, unit: ' °C' }))

  const telemetryChart = useMemo(() => buildTelemetryChart(activePoints, TELEMETRY_SERIES), [activePoints])
  const cellChart = useMemo(() => buildTelemetryChart(activePoints, cellSeriesDefs), [activePoints])
  const ntcChart = useMemo(() => buildTelemetryChart(activePoints, ntcSeriesDefs), [activePoints])

  const latestPointMs = activePoints.length ? activePoints[activePoints.length - 1].epochMs : undefined
  const chartDomain = useMemo(
    () => (isRealtime
      ? (latestPointMs !== undefined ? [latestPointMs - WINDOW_MS, latestPointMs] : undefined)
      : (timeRange.from && timeRange.to ? [timeRange.from.getTime(), timeRange.to.getTime()] : undefined)),
    [isRealtime, latestPointMs, timeRange.from, timeRange.to],
  )
  const chartTickCount = 10

  // Identifies the applied range: the label alone isn't enough for custom
  // ranges, and the bounds alone aren't enough to tell "Realtime" from a quick
  // range that happens to resolve to the same window. Only a new selection
  // changes this — a refetch reuses the same range.
  const rangeKey = `${timeRange.label}|${timeRange.from?.getTime() ?? ''}|${timeRange.to?.getTime() ?? ''}`

  // Live socket tick wins once it streams; before that it's the matched node's
  // lastSeen from devicesLatest (then the BMS-latest fallback). deviceInfo.ts is
  // epoch seconds, so scale to ms; lastSeen is an ISO string.
  const seededLastSeen = nodeLastSeen ?? bms.lastSeen
  const lastUpdatedAt = deviceTs !== undefined
    ? formatDateTime(deviceTs * 1000)
    : seededLastSeen ? formatDateTime(seededLastSeen) : '--'

  return (
    <div className="w-full h-full bg-[#f2f4f7] flex flex-col">
      <div className="relative bg-[#f9fafa] flex items-center justify-between px-4 py-3 border-b border-[#dfe4ed] shrink-0">
        <div className="flex items-center gap-3">
          <button
            className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-lg"
            // Explicit target (matches this page's own breadcrumb parent)
            // rather than navigate(-1) — see ViewDashboard.jsx/HistoricalData.jsx
            // for why relative history position breaks this kind of back button.
            onClick={() => navigate(`/ViewDashboard/${deviceId}`)}
          >
            <span className="material-icons text-xl text-[#272d37]">
              arrow_back
            </span>
          </button>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-[#272d37]">
              BMS - {bms.bmsId ?? ecuid}
            </span>
            <span className="text-sm font-normal text-[#616a79]">
              Slot: {bms.sourceAddress ?? ecuid} • {bms.organization}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-normal text-[#252a34]">
            Last updated at: {lastUpdatedAt}
          </span>
          <button
            className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-lg"
            onClick={handleRefetch}
          >
            <span className="material-icons text-xl text-[#272d37]">sync</span>
          </button>
        </div>
        {isRefetching && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden">
            <div className="h-full w-1/3 bg-[#27b83e] animate-refetch-line" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 p-4">
        <div className="flex gap-3 items-start">
          <div className="flex-1 flex flex-col gap-3 self-stretch">
            <div className="flex gap-3 items-stretch w-full">
              <StatusOverviewCard bms={bms} className="flex-1" />
              <IndicatorsCard bms={bms} className="flex-1" />
            </div>
            <FaultsCard faults={bms.faults ?? []} className="flex-1 w-full min-h-0" />
          </div>

          <div className="flex-1 flex flex-col gap-3">
            <div className="flex gap-3 items-stretch w-full">
              <CapacityCard bms={bms} className="flex-1" />
              <TemperatureStatsCard bms={bms} className="flex-1" />
            </div>
            <div className="flex gap-3 items-start w-full">
              <FlagsAndFaultsCard bms={bms} className="flex-1" />
              <CycleInfoCard bms={bms} className="flex-1 self-stretch" />
            </div>
            <div className="flex gap-3 items-start w-full">
              <VersionInfoCard bms={bms} className="flex-1" />
              <ConfigsCard bms={bms} className="flex-1 self-stretch" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-[63fr_37fr] gap-3">
          <CellVoltagesCard
            cellVoltages={bms.cellVoltages ?? []}
          />
          <NtcTemperaturesCard
            ntcTemperatures={bms.ntcTemperatures ?? []}
          />
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-[#252a34]">
            Telemetry Over Time
          </span>
          <TimeRangeDropdown value={timeRange.label} onChange={setTimeRange} />
        </div>

        <SocPackVoltageChart
          chart={telemetryChart}
          domain={chartDomain}
          tickCount={chartTickCount}
          isLoading={isHistoryLoading}
          error={historyError}
          onRetry={handleRefetch}
        />

        <div className="grid grid-cols-2 gap-3">
          <TelemetryChart
            title="Cell Voltages"
            chart={cellChart}
            height={220}
            domain={chartDomain}
            tickCount={chartTickCount}
            isLoading={isHistoryLoading}
            error={historyError}
            onRetry={handleRefetch}
          />
          <TelemetryChart
            title="NTC Temperatures"
            chart={ntcChart}
            height={220}
            domain={chartDomain}
            tickCount={chartTickCount}
            isLoading={isHistoryLoading}
            error={historyError}
            onRetry={handleRefetch}
          />
        </div>

        <AllDataTable
          points={activePoints}
          exportName={ecuid}
          rangeKey={rangeKey}
          isLoading={isHistoryLoading}
          error={historyError}
          onRetry={handleRefetch}
        />
      </div>
    </div>
  );
}

export default BmsDashboard
